import React, { useMemo, useState } from 'react';
import { type Suspect } from '../../api/types.js';
import { fetchAuditLogById } from '../../api/index.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import DebuggingModal from '../DebuggingModal.js';
import { useSettings } from '../../SettingsProvider.js';
import moment from 'moment-timezone';

interface AuditTabProps {
  suspects: Record<string, Suspect>;
}

type SortCol = 'updatedAt' | 'analysisStatus' | 'deviceId';

function AuditTab({ suspects }: AuditTabProps) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [filterText, setFilterText] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('updatedAt');
  const [sortDesc, setSortDesc] = useState(true);

  const suspectsArray = useMemo(() => {
    const arr = Object.values(suspects);

    const filtered = filterText.trim()
      ? arr.filter((e) => e.deviceId?.toLowerCase().includes(filterText.trim().toLowerCase()))
      : arr;

    return filtered.sort((a, b) => {
      if (sortCol === 'deviceId') {
        const av = a.deviceId ?? '';
        const bv = b.deviceId ?? '';
        return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      if (sortCol === 'analysisStatus') {
        const av = a.analysisStatus ?? 'PENDING';
        const bv = b.analysisStatus ?? 'PENDING';
        return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      const cmp = moment(b.updatedAt).isAfter(a.updatedAt) ? 1 : -1;
      return sortDesc ? cmp : -cmp;
    });
  }, [suspects, filterText, sortCol, sortDesc]);

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDesc((d) => !d);
    else {
      setSortCol(col);
      setSortDesc(true);
    }
  };

  const arrow = (col: SortCol) => (sortCol === col ? (sortDesc ? ' ↓' : ' ↑') : '');

  return (
    <div className="sp on">
      <DebuggingModal
        fetchTaskSteps={(type, id) => fetchAuditLogById(type, id)}
        open={!!selectedEntity}
        onClose={() => setSelectedEntity('')}
        entityType={'PROFILE'}
        entityId={selectedEntity}
      />
      <div className="sp-title">{t('audit.title')}</div>
      <div className="sp-subtitle">{t('audit.subtitle')}</div>

      <div className="sg">
        <div className="sg-title">{t('audit.recentEvents')}</div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            className="fi"
            style={{ flex: 1 }}
            placeholder={t('audit.filterPlaceholder')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {suspects === null && <div className="status-meta">{t('audit.loading')}</div>}

        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 14px 80px 1fr 100px',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderBottom: '1px solid var(--bm)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--t3)',
              background: 'var(--bg-card)',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <span
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                color: sortCol === 'updatedAt' ? 'var(--gold)' : undefined,
              }}
              onClick={() => toggleSort('updatedAt')}
            >
              {t('audit.colUpdatedAt')}
              {arrow('updatedAt')}
            </span>
            <span />
            <span
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                color: sortCol === 'analysisStatus' ? 'var(--gold)' : undefined,
              }}
              onClick={() => toggleSort('analysisStatus')}
            >
              {t('audit.colStatus')}
              {arrow('analysisStatus')}
            </span>
            <span
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                color: sortCol === 'deviceId' ? 'var(--gold)' : undefined,
              }}
              onClick={() => toggleSort('deviceId')}
            >
              {t('audit.colDeviceId')}
              {arrow('deviceId')}
            </span>
            <span />
          </div>

          {suspectsArray.map((e, i) => {
            let color = 'gold';
            if (e.analysisStatus === 'FAILED') color = 'red';
            else if (e.analysisStatus === 'EMPTY') color = 'blue';
            else if (e.analysisStatus === 'SUCCESS') color = 'green';
            else if (e.analysisStatus === 'IN_PROGRESS') color = 'blue';

            return (
              <div key={i} className="audit-row" onClick={() => setSelectedEntity(e.auditId)}>
                <span className="audit-time">
                  {moment
                    .tz(e.updatedAt, settings?.general.timezone ?? 'UTC')
                    .format(`${settings?.general.dateFormat ?? 'YYYY-MM-DD'} HH:mm:ss z`)}
                </span>
                <span className={`audit-dot ${color}`} />
                <span className={`audit-status ${color}`}>{e.analysisStatus ?? 'PENDING'}</span>
                <span className="audit-text">{e.deviceId}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AuditTab;
